import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import { keepPinnedColumnsInPlace } from "./pinned-columns.ts"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  horizontalListSortingStrategy
} from "@dnd-kit/sortable"
import { Card, Flex, Table, type TablePaginationConfig, Typography } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { reorderConfig } from "../../../../../../../components/EntityTable/entity-table.utils.ts"
import { ResizableHeaderCell } from "../../../../../../../components/Table/ResizableHeaderCell.tsx"
import { TableAddAction } from "../../../../../../../components/Table/TableAddAction.tsx"
import { TableResultCount } from "../../../../../../../components/Table/TableResultCount.tsx"
import { TableColumnConfigModal } from "../../../../../../../components/TableColumnConfigModal"
import { useColumnResize } from "../../../../../../../hooks/use-column-resize.tsx"
import { useTableColumnConfig } from "../../../../../../../hooks/use-table-column-config.tsx"
import { useInfiniteScroll } from "../../../../../../../hooks/use-table-infinite-scroll.tsx"
import { cn } from "../../../../../../../utils/cn.ts"
import { DATE_FORMAT, getNow } from "../../../../../../../utils/date/date.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { getTableOrderingFromSorter } from "../../../../../../../utils/table"
import { type PermissionProjectPermissions } from "../../../../../../SettingsPermissions/types/permissions.types.ts"
import type { TabKey } from "../../../../../../User/redux/ui-preferences.types.ts"
import type {
  FinancialTransactionItem,
  FinancialTransactionSummary
} from "../../../../../types/project-finance.types.tsx"
import {
  ENTRY_TYPES,
  FINANCE_ITEM_TYPES
} from "../../../../constants/finance-item-constants.ts"
import { PaymentFormModal } from "./PaymentFormModal.tsx"
import { ProjectViewFinanceNewRow } from "./ProjectViewFinanceNewRow.tsx"
import { TransactionPaymentsDrawer } from "./TransactionPaymentsDrawer.tsx"
import { getColumns } from "./columns.tsx"
import { useCloseRowPopupsOnHScroll } from "./finance-row-popups.ts"
import type { PaymentModalState } from "./payment-modal.types.ts"

const NEW_ROW_ID = "__new_transaction__"

// Module-level so the sensor descriptor is stable across renders (a fresh
// options literal would miss dnd-kit's useSensor/useSensors memo every render).
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } }

const ROW_CLICK_IGNORE_SELECTOR =
  "button, a, input, .ant-select, .ant-dropdown, .ant-dropdown-trigger, .ant-upload, .ant-checkbox, [data-stop-row-click]"

/**
 * Guards row double-click-to-edit against clicks on interactive controls.
 *
 * AntD overlays (the Actions dropdown menu, date pickers) render in a body
 * portal, so their events bubble to the row even though their DOM lives outside
 * it — `rowElement.contains` rejects those before the selector check. Exported
 * so the portal-vs-row logic can be unit-tested without the connected table.
 */
export function shouldIgnoreRowClick(
  target: HTMLElement | null,
  rowElement: HTMLElement
): boolean {
  if (!target || !rowElement.contains(target)) return true
  return !!target.closest(ROW_CLICK_IGNORE_SELECTOR)
}

/**
 * Lays out the finance-table summary footer: how far the "Summary" label cell
 * spans, where the totals cell starts, and how wide it is. Pure and exported so
 * the colspan math (incl. the colSpan={0} guard) can be unit-tested without the
 * full connected list.
 */
export function computeSummaryColumnLayout(
  visibleColumns: { key?: React.Key }[]
) {
  const findColumnIndex = (key: string) =>
    visibleColumns.findIndex((column) => column.key === key)

  const statusIndex = findColumnIndex("status")
  const paymentsIndex = findColumnIndex("payments")
  const attachmentsIndex = findColumnIndex("attachments")
  const actionsIndex = findColumnIndex("actions")

  const totalsIndex = statusIndex > -1 ? statusIndex : visibleColumns.length - 1
  const lastFixedIndex = Math.max(
    totalsIndex,
    paymentsIndex,
    attachmentsIndex,
    actionsIndex
  )

  return {
    // Guard against colSpan={0}, which AntD treats as "drop the cell": if the
    // totals/status column ever became the first visible column, totalsIndex
    // would be 0 and the "Summary" label cell would vanish.
    summaryColSpan: Math.max(1, totalsIndex),
    totalsIndex,
    totalsColSpan: lastFixedIndex - totalsIndex + 1
  }
}

interface RowProps
  extends
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLTableRowElement>,
      HTMLTableRowElement
    >,
    Pick<ProjectViewFinanceListProps, "onSelectRow"> {
  record: FinancialTransactionItem
}

interface ProjectViewFinanceListProps {
  userId: string
  projectPermissions: PermissionProjectPermissions
  userDateFormat: string
  projectId: string
  financialTransactionItems: FinancialTransactionItem[]
  financialSummary: FinancialTransactionSummary
  fetching: boolean
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  count: number
  itemType: number | null
  editableRow?: string
  isAdding?: boolean
  options?: Record<string, BaseOptionType[]>
  configOpen?: boolean
  generateInvoiceAllowed?: boolean
  onAddTransaction: () => void
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: FinancialTransactionItem, action: string) => void
  onPageChange: (page: number) => void
  onLoadMore: () => void
  onOpenColumnConfig?: () => void
  onCloseColumnConfig?: () => void
  onRefresh?: () => void
  // False only for a tour/demo preview reusing this real table on fake data —
  // its column edits stay local instead of overwriting every real project's
  // saved Finance layout, which is keyed by this same surface for every
  // account. Defaults to true so every real caller is unaffected.
  persistColumnConfig?: boolean
}

export function ProjectViewFinanceList({
  userId,
  userDateFormat,
  projectPermissions,
  financialTransactionItems,
  financialSummary,
  projectId,
  next,
  retryNext,
  count,
  fetching,
  itemType,
  editableRow = "",
  isAdding = false,
  options = {},
  configOpen = false,
  generateInvoiceAllowed = false,
  onAddTransaction,
  onLoadMore,
  onOrderingChange,
  onSelectRow = () => {},
  onPageChange,
  onCloseColumnConfig = () => {},
  onRefresh,
  persistColumnConfig = true
}: ProjectViewFinanceListProps) {
  const { t } = useTranslation()
  const moduleDocs = projectPermissions?.docs
  const moduleAccess = projectPermissions?.finances

  const isLocked = !!editableRow || isAdding
  const projectTypes = useMemo(
    () => options?.project_type || [],
    [options?.project_type]
  )

  const deleteAllowed = moduleAccess?.project_finance_delete
  const editAllowed = moduleAccess?.project_finance_edit
  const createAllowed = moduleAccess?.project_finance_create
  const invoiceCreateAllowed =
    projectPermissions?.invoices?.project_invoice_create
  const invoiceSelfAssigned =
    !!projectPermissions?.invoices?.project_invoice_self_assigned
  const uploadFileAllowed = moduleDocs?.project_doc_create
  const deleteFileAllowed = moduleDocs?.project_doc_delete
  const deleteForeignFileAllowed =
    deleteFileAllowed && moduleDocs?.project_doc_manage_others

  const tabKey: TabKey =
    itemType === FINANCE_ITEM_TYPES.RECEIVABLE
      ? "receivable"
      : itemType === FINANCE_ITEM_TYPES.PAYABLE
        ? "payable"
        : "all"

  const tabLabel =
    tabKey === "all"
      ? "All"
      : tabKey === "receivable"
        ? "Receivable"
        : "Payable"

  const [paymentModal, setPaymentModal] = useState<{
    state: PaymentModalState
    record: FinancialTransactionItem
  } | null>(null)

  const [drawerRecordId, setDrawerRecordId] = useState<string | null>(null)
  const liveDrawerRecord = useMemo(
    () =>
      financialTransactionItems.find((r) => r.id === drawerRecordId) ?? null,
    [financialTransactionItems, drawerRecordId]
  )
  const lastDrawerRecordRef = useRef<FinancialTransactionItem | null>(null)
  useEffect(() => {
    if (liveDrawerRecord) lastDrawerRecordRef.current = liveDrawerRecord
  }, [liveDrawerRecord])
  const drawerRecord = liveDrawerRecord ?? lastDrawerRecordRef.current
  const closeDrawer = useCallback(() => {
    setDrawerRecordId(null)
    lastDrawerRecordRef.current = null
  }, [])

  useEffect(() => {
    if (drawerRecordId && !liveDrawerRecord && !fetching) {
      closeDrawer()
    }
  }, [drawerRecordId, liveDrawerRecord, fetching, closeDrawer])

  useEffect(() => {
    if (isLocked) closeDrawer()
  }, [isLocked, closeDrawer])

  const tableData = useMemo(() => {
    const results = financialTransactionItems || []

    if (isAdding) {
      const date = getNow().format(DATE_FORMAT)

      const newTransaction = {
        id: NEW_ROW_ID,
        date: date,
        due_date: date,
        item_type: itemType,
        entry_type: ENTRY_TYPES.PAYMENT,
        project_type: projectTypes?.length === 1 ? projectTypes[0].value : null
      } as FinancialTransactionItem

      return [newTransaction, ...results] as FinancialTransactionItem[]
    }

    return results
  }, [financialTransactionItems, isAdding, itemType, projectTypes])

  const columns = useMemo(
    () =>
      getColumns({
        t,
        currentUserId: userId,
        projectId,
        userDateFormat,
        isLocked,
        isAdding,
        deleteAllowed,
        editAllowed,
        uploadFileAllowed,
        deleteForeignFileAllowed,
        deleteFileAllowed,
        generateInvoiceAllowed,
        onSelectRow,
        onCompletePayment: (record) =>
          setPaymentModal({ state: { mode: "complete" }, record }),
        onOpenPayments: (record) => setDrawerRecordId(record.id)
      }),
    [
      t,
      userId,
      projectId,
      generateInvoiceAllowed,
      userDateFormat,
      isLocked,
      isAdding,
      deleteAllowed,
      editAllowed,
      uploadFileAllowed,
      deleteForeignFileAllowed,
      deleteFileAllowed,
      onSelectRow,
      setDrawerRecordId
    ]
  )

  const baseColumns = useMemo(
    () => columns.filter((column) => !column.hidden),
    [columns]
  )

  const {
    columns: configuredColumns,
    fullConfig,
    setColumns,
    resetToDefaults
  } = useTableColumnConfig(
    "financial_transactions",
    tabKey,
    baseColumns,
    undefined,
    persistColumnConfig
  )

  const tableColumns = useMemo(
    () =>
      isLocked
        ? baseColumns
        : keepPinnedColumnsInPlace(configuredColumns, baseColumns),
    [isLocked, baseColumns, configuredColumns]
  )

  const { containerWidth, tableWrapRef, decorateColumns, resetWidths } =
    useColumnResize<FinancialTransactionItem>({
      fullConfig,
      setColumns,
      enabled: !isLocked,
      reorderable: !isLocked,
      minWidth: 130
    })

  const resizableColumns = useMemo(
    () => decorateColumns(tableColumns),
    [decorateColumns, tableColumns]
  )

  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS))

  const sortableKeys = useMemo(
    () =>
      tableColumns
        .filter((col) => col.key != null && !col.fixed)
        .map((col) => String(col.key)),
    [tableColumns]
  )

  const onColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      setColumns(reorderConfig(fullConfig, String(active.id), String(over.id)))
    },
    [fullConfig, setColumns]
  )

  const summaryColumnLayout = useMemo(
    () => computeSummaryColumnLayout(tableColumns),
    [tableColumns]
  )

  const tableScrollX = useMemo(() => {
    const columnsWidth = resizableColumns.reduce(
      (sum, column) => sum + (Number(column.width) || 150),
      0
    )
    // Never narrower than the viewport, else columns detach from the fixed-right ones.
    return Math.max(columnsWidth, containerWidth)
  }, [resizableColumns, containerWidth])

  const configuredFixedRightWidth = useMemo(
    () =>
      tableColumns.reduce(
        (sum, column) =>
          column.fixed === "right" ? sum + (Number(column.width) || 0) : sum,
        0
      ),
    [tableColumns]
  )

  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const [summaryViewportWidth, setSummaryViewportWidth] = useState<number>()
  const [measuredFixedRightWidth, setMeasuredFixedRightWidth] =
    useState<number>()

  useEffect(() => {
    const wrapper = tableWrapperRef.current
    const body = wrapper?.querySelector<HTMLElement>(".ant-table-body")
    if (!wrapper || !body) return

    const fixedHeaderCells = wrapper.querySelectorAll<HTMLElement>(
      ".ant-table-thead th.ant-table-cell-fix-right"
    )

    const measure = () => {
      setSummaryViewportWidth(body.clientWidth)
      let fixedWidth = 0
      fixedHeaderCells.forEach((cell) => {
        fixedWidth += cell.getBoundingClientRect().width
      })
      setMeasuredFixedRightWidth(fixedWidth || undefined)
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(body)
    fixedHeaderCells.forEach((cell) => observer.observe(cell))
    return () => observer.disconnect()
  }, [tableColumns])

  const fixedRightWidth = measuredFixedRightWidth ?? configuredFixedRightWidth

  const onChangeTable = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<FinancialTransactionItem>
        | SorterResult<FinancialTransactionItem>[]
    ) => {
      const ordering = getTableOrderingFromSorter(sorter)

      onPageChange(pagination.current || 1)
      onOrderingChange(ordering)
    },
    [onPageChange, onOrderingChange]
  )

  const RowComponent = useCallback(
    ({ record, onSelectRow, ...props }: RowProps) => {
      const isNewRow = record?.id === NEW_ROW_ID && isAdding
      const isEditRow = record?.id === editableRow && editableRow

      if (isNewRow || isEditRow) {
        return (
          <ProjectViewFinanceNewRow
            record={record}
            columns={tableColumns}
            options={options}
            mode={isNewRow ? "create" : "edit"}
            invoiceCreateAllowed={invoiceCreateAllowed}
            invoiceSelfAssigned={invoiceSelfAssigned}
            userId={userId}
            onSelectRow={onSelectRow}
            {...props}
          />
        )
      }

      return <tr {...props}>{props?.children}</tr>
    },
    [
      editableRow,
      isAdding,
      options,
      invoiceCreateAllowed,
      invoiceSelfAssigned,
      userId,
      tableColumns
    ]
  )

  const { onTableScroll } = useInfiniteScroll({
    loading: fetching,
    hasMore: !!next,
    loadMoreKey: next,
    loadMore: onLoadMore,
    disabled: isLocked,
    containerRef: tableWrapRef
  })
  const onScroll = useCloseRowPopupsOnHScroll(onTableScroll)

  return (
    <>
      <TableAddAction
        label={t("table.addRow.financialLine")}
        allowed={!!createAllowed}
        disabled={isLocked}
        onAdd={onAddTransaction}
      >
        <Card classNames={{ body: "p-0" }}>
          <div ref={tableWrapperRef}>
            <div ref={tableWrapRef}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={onColumnDragEnd}
              >
                <SortableContext
                  items={sortableKeys}
                  strategy={horizontalListSortingStrategy}
                >
                  <Table<FinancialTransactionItem>
                    rowKey="id"
                    size="small"
                    rowHoverable={false}
                    onScroll={onScroll}
                    className={cn(
                      "[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white",
                      {
                        "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
                          !tableData?.length
                      }
                    )}
                    columns={resizableColumns}
                    loading={fetching && !financialTransactionItems?.length}
                    dataSource={tableData}
                    scroll={{
                      x: tableScrollX,
                      y: 600,
                      scrollToFirstRowOnChange: true
                    }}
                    onChange={onChangeTable}
                    pagination={false}
                    onRow={(record): RowProps => ({
                      record,
                      onSelectRow,
                      onDoubleClick: editAllowed
                        ? (e) => {
                            if (isLocked || record.id === NEW_ROW_ID) return
                            if (
                              shouldIgnoreRowClick(
                                e.target as HTMLElement,
                                e.currentTarget as HTMLElement
                              )
                            )
                              return
                            closeDrawer()
                            onSelectRow(record, "edit_row")
                          }
                        : undefined
                    })}
                    components={{
                      header: { cell: ResizableHeaderCell },
                      body:
                        (editAllowed || createAllowed) && tableData?.length
                          ? { row: RowComponent }
                          : {}
                    }}
                    summary={() => {
                      const formatedReceivable = budgetFormat(
                        financialSummary?.receivable
                      )
                      const formatedReceivableCollected = budgetFormat(
                        financialSummary?.receivable_collected
                      )
                      const formatedPayable = budgetFormat(
                        financialSummary?.payable
                      )
                      const formatedPayablePaid = budgetFormat(
                        financialSummary?.payable_paid
                      )

                      const singleTotal =
                        financialSummary?.receivable ||
                        financialSummary?.payable
                      const singleSettled = financialSummary?.receivable
                        ? financialSummary?.receivable_collected
                        : financialSummary?.payable_paid

                      const { summaryColSpan, totalsIndex, totalsColSpan } =
                        summaryColumnLayout

                      const summaryContentWidth =
                        summaryViewportWidth != null
                          ? Math.max(0, summaryViewportWidth - fixedRightWidth)
                          : undefined

                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row className="finance-table-summary-row">
                            <Table.Summary.Cell
                              index={0}
                              colSpan={summaryColSpan}
                              className="!px-2"
                            >
                              <div
                                className="flex items-center gap-2"
                                style={{
                                  position: "sticky",
                                  left: 0,
                                  width: summaryContentWidth
                                }}
                              >
                                <Typography.Text strong className="shrink-0">
                                  Summary
                                </Typography.Text>
                                <div className="min-w-0 flex-1">
                                  {/* mx-auto collapses on overflow: a too-wide block spills right, never left over the label */}
                                  <div className="mx-auto w-max whitespace-nowrap">
                                    {financialSummary?.receivable &&
                                    financialSummary?.payable ? (
                                      <Flex vertical align="center">
                                        <Flex gap="small">
                                          <Typography.Text>
                                            Receivable:
                                          </Typography.Text>
                                          <Typography.Text strong>
                                            {formatedReceivableCollected} /{" "}
                                            {formatedReceivable}
                                          </Typography.Text>
                                        </Flex>
                                        <Flex gap="small">
                                          <Typography.Text>
                                            Payable:
                                          </Typography.Text>
                                          <Typography.Text strong>
                                            {formatedPayablePaid} /{" "}
                                            {formatedPayable}
                                          </Typography.Text>
                                        </Flex>
                                      </Flex>
                                    ) : (
                                      <Typography.Text strong>
                                        {budgetFormat(singleSettled)} /{" "}
                                        {budgetFormat(singleTotal)}
                                      </Typography.Text>
                                    )}
                                  </div>
                                </div>
                                <Typography.Text
                                  strong
                                  aria-hidden
                                  className="invisible min-w-0 shrink overflow-hidden"
                                >
                                  Summary
                                </Typography.Text>
                              </div>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                              index={totalsIndex}
                              colSpan={totalsColSpan}
                            />
                          </Table.Summary.Row>
                        </Table.Summary>
                      )
                    }}
                  />
                </SortableContext>
              </DndContext>
            </div>
            <TableResultCount
              underAddAction
              loaded={financialTransactionItems?.length ?? 0}
              total={count}
              loading={fetching}
              next={next}
              retryNext={retryNext}
              onRetry={onLoadMore}
            />
          </div>
          {/* Alone among the five, this tab ends on a hint rather than the
              count — so this is the row the floating "+" rests on, and this is
              the row that has to keep the corner free. */}
          <div className="py-2 pl-3 pr-32">
            <Typography.Text type="secondary" className="text-xs">
              Click “Payments” to view payment history, add a payment, or
              release retention.
            </Typography.Text>
          </div>
        </Card>
      </TableAddAction>
      <TableColumnConfigModal
        open={configOpen}
        title={`Configuring: ${tabLabel}`}
        availableColumns={columns}
        currentConfig={fullConfig}
        onSave={(next) => setColumns(next)}
        onReset={() => {
          resetWidths()
          resetToDefaults()
        }}
        onClose={onCloseColumnConfig}
      />
      {paymentModal && (
        <PaymentFormModal
          state={paymentModal.state}
          record={paymentModal.record}
          projectId={projectId}
          userId={userId}
          invoiceCreateAllowed={invoiceCreateAllowed}
          invoiceSelfAssigned={invoiceSelfAssigned}
          uploadFileAllowed={uploadFileAllowed}
          onClose={() => setPaymentModal(null)}
          onSaved={() => {
            setPaymentModal(null)
            onRefresh?.()
          }}
        />
      )}
      <TransactionPaymentsDrawer
        record={drawerRecord}
        open={drawerRecordId !== null}
        projectId={projectId}
        currentUserId={userId}
        paymentEditAllowed={editAllowed}
        uploadFileAllowed={uploadFileAllowed}
        deleteFileAllowed={deleteFileAllowed}
        deleteForeignFileAllowed={deleteForeignFileAllowed}
        onClose={closeDrawer}
        onOpenPaymentModal={(state) =>
          drawerRecord && setPaymentModal({ state, record: drawerRecord })
        }
        onPaymentChanged={() => onRefresh?.()}
      />
    </>
  )
}

export default ProjectViewFinanceList
